import { getSystemDb } from '@firesquirrel/local-db';
import { api } from '../apiClient';
import { writeAuditLog } from './systemAuditLogService';

export async function listFeatureFlags(params?: { category?: string; status?: string; search?: string }): Promise<any[]> {
  const db = await getSystemDb();
  const docs = await db.feature_flags.toArray();
  let results = docs as any[];
  if (params?.category) results = results.filter(f => f.category === params.category);
  if (params?.status) results = results.filter(f => f.status === params.status);
  if (params?.search) {
    const q = params.search.toLowerCase();
    results = results.filter(f => f.key?.toLowerCase().includes(q) || f.name?.toLowerCase().includes(q));
  }
  return results;
}

export async function getFeatureFlag(keyOrId: string): Promise<any | null> {
  const db = await getSystemDb();
  let doc = await db.feature_flags.get(keyOrId);
  if (!doc) {
    const all = await listFeatureFlags();
    const match = all.find((f: any) => f.key === keyOrId);
    return match ?? null;
  }
  return doc;
}

export async function upsertFeatureFlag(data: any): Promise<void> {
  const db = await getSystemDb();
  await db.feature_flags.put({ ...data, updatedAt: new Date().toISOString() });
}

export async function createFeatureFlag(data: {
  key: string;
  name: string;
  category: string;
  description?: string;
  minTier?: string | null;
  status?: string;
  defaultValue?: boolean;
}): Promise<any> {
  const result = await api.featureFlags.create({
    ...data,
    defaultValue: data.defaultValue ?? true,
    status: data.status ?? 'disabled',
  });
  await upsertFeatureFlag(result);
  writeAuditLog({
    collection: 'featureFlags',
    documentId: result._id,
    action: 'create',
    summary: `Created feature flag "${result.key}"`,
  }).catch((err) => {
    console.warn('[featureFlagService] Failed to write audit log for feature flag creation:', err);
  });
  return result;
}

export async function updateFeatureFlagStatus(id: string, status: string): Promise<any> {
  const result = await api.featureFlags.updateStatus(id, status);
  await upsertFeatureFlag(result);
  writeAuditLog({
    collection: 'featureFlags',
    documentId: id,
    action: 'statusChange',
    summary: `Feature flag "${result.key}" status changed to ${status}`,
  }).catch((err) => {
    console.warn('[featureFlagService] Failed to write audit log for feature flag status change:', err);
  });
  return result;
}

export async function updateFeatureFlag(id: string, data: any): Promise<any> {
  const result = await api.featureFlags.update(id, data);
  await upsertFeatureFlag(result);
  writeAuditLog({
    collection: 'featureFlags',
    documentId: id,
    action: 'update',
    summary: `Updated feature flag "${result.key}"`,
  }).catch((err) => {
    console.warn('[featureFlagService] Failed to write audit log for feature flag update:', err);
  });
  return result;
}

export async function deleteFeatureFlag(id: string): Promise<void> {
  let key = id;
  const db = await getSystemDb();
  const existing = await db.feature_flags.get(id);
  if (existing) key = (existing as any).key ?? id;
  try {
    await (api.featureFlags as any).delete?.(id);
  } catch {
    /* best-effort API call — remove from local cache regardless */
  }
  if (existing) {
    const now = new Date().toISOString();
    await db.feature_flags.put({ ...(existing as any), _deleted: true, deletedAt: now, updatedAt: now });
  }
  writeAuditLog({
    collection: 'featureFlags',
    documentId: id,
    action: 'delete',
    summary: `Deleted feature flag "${key}"`,
  }).catch((err) => {
    console.warn('[featureFlagService] Failed to write audit log for feature flag delete:', err);
  });
}
